'use client';

import { Sparkles, Wand2 } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysSpacesCTAProps {
  variant?: 'banner' | 'card' | 'inline';
  locale?: string;
  messages?: Record<string, unknown>;
  className?: string;
}

/**
 * AI Spaces CTA Component
 * Promotes the portal-integrated Room Designer across different pages
 */
export const AllysSpacesCTA: React.FC<AllysSpacesCTAProps> = ({ 
  variant = 'banner',
  locale: localeProp,
  messages,
  className = ''
}) => {
  const pathname = usePathname();
  const spacesMsg = messages?.spaces as Record<string, unknown> | undefined;
  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = localeProp || match?.[1] || 'en';
  const spacesUrl = `/${locale}/account/spaces`;

  if (variant === 'inline') {
    return (
      <Link
        href={spacesUrl}
        className={`inline-flex items-center gap-2 px-4 py-2 bg-linear-to-r from-amber-500 to-yellow-500 text-stone-900 rounded-full font-semibold text-sm hover:shadow-lg hover:scale-105 transition-all ${className}`}
      >
        <Wand2 size={16} />
        <span>{safeGetString(spacesMsg?.tryAIRoomDesign, 'Try AI Room Design')}</span>
      </Link>
    );
  }

  if (variant === 'card') {
    return (
      <div className={`bg-linear-to-br from-stone-900 via-stone-800 to-stone-900 rounded-2xl p-6 border border-amber-500/20 ${className}`}>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-linear-to-br from-amber-500 to-yellow-500 rounded-xl shrink-0">
            <Wand2 className="w-6 h-6 text-stone-900" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">
              {safeGetString(spacesMsg?.reimagineTitle, 'Reimagine This Space with AI')}
            </h3>
            <p className="text-stone-400 text-sm mb-4">
              {safeGetString(spacesMsg?.reimagineDescription, 'Upload a photo and let AI transform it with stunning interior design styles.')}
            </p>
            <Link
              href={spacesUrl}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-linear-to-r from-amber-500 to-yellow-500 text-stone-900 rounded-full font-semibold text-sm hover:shadow-lg hover:shadow-amber-500/25 hover:scale-105 transition-all"
            >
              <Sparkles size={16} />
              <span>{safeGetString(spacesMsg?.tryAISpaces, 'Try AI Spaces')}</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Default: banner variant
  return (
    <section className={`relative overflow-hidden ${className}`}>
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-linear-to-br from-stone-900 via-[#1a1a2e] to-stone-900" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          {/* Icon */}
          <div className="shrink-0">
            <div className="relative">
              <div className="absolute inset-0 bg-linear-to-r from-amber-500 to-yellow-500 rounded-2xl blur-xl opacity-50 animate-pulse" />
              <div className="relative p-5 bg-linear-to-br from-amber-500 to-yellow-500 rounded-2xl">
                <Wand2 className="w-10 h-10 text-stone-900" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-xs font-medium mb-4">
              <Sparkles size={12} />
              <span>{safeGetString(spacesMsg?.aiPowered, 'AI-Powered')}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
              {safeGetString(spacesMsg?.transformTitle, 'Transform Any Space with AI')}
            </h2>
            <p className="text-stone-400 text-base lg:text-lg max-w-2xl">
              {safeGetString(spacesMsg?.transformDescription, 'Upload a photo of any room and watch as our AI reimagines it with stunning interior design styles, color palettes, and layouts. Visualize your dream home before making any changes.')}
            </p>
          </div>

          {/* CTA Button */}
          <div className="shrink-0">
            <Link
              href={spacesUrl}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-linear-to-r from-amber-500 to-yellow-500 text-stone-900 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-amber-500/30 hover:scale-105 transition-all duration-300"
            >
              <span>{safeGetString(spacesMsg?.tryAISpaces, 'Try AI Spaces')}</span>
              <svg 
                className="w-5 h-5 group-hover:translate-x-1 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
